import { Flex, Tag, Tooltip } from "@chakra-ui/react";
import React, { FC, useMemo } from "react";

import { formatDate, formatDuration } from "../../../../utils/datetime";
import type { SerializableFilterState } from "./customInsightsChatUtils";

type FilterSummaryItem = { label: string; value: string; hoverText?: string };

const DEFAULT_DURATION_MIN = 120; // 2 minutes
const DEFAULT_DURATION_MAX = 7200; // 2 hours

type DisplayableItem = {
  displayTitle?: string;
  label?: string | null;
  value?: string;
};

function getMultiFilterOptions(
  label: string,
  selectedOptions: readonly DisplayableItem[]
): FilterSummaryItem {
  const strings = selectedOptions
    .map((v) => v.displayTitle || v.label || v.value || "")
    .filter(Boolean);
  if (strings.length === 0) {
    return { label, value: "None" };
  }

  const showAllValues = strings.length <= 2;
  const value = showAllValues
    ? strings.join(", ")
    : `${strings[0]}, ${strings[1]}, & ${strings.length - 2} more`;
  return {
    label,
    value,
    hoverText: showAllValues ? undefined : strings.join("\n"),
  };
}

function toDateString(d: Date | string): string {
  return d instanceof Date ? d.toISOString() : String(d);
}

/* eslint-disable camelcase -- keys match SelectDateRangeState["type"] */
const DATE_RANGE_PRESET_LABELS: Record<string, string> = {
  last_6m: "Last 6 months",
  last_90: "Last 90 days",
  last_30: "Last 30 days",
  last_7: "Last 7 days",
};
/* eslint-enable camelcase */

function buildFilterSummary(
  filters: SerializableFilterState,
  compact = false,
  nonDefaultOnly = false
): FilterSummaryItem[] {
  const summarizedFilters: FilterSummaryItem[] = [];
  const dateFormatOpts = {
    year: "numeric" as const,
    month: "short" as const,
    day: "numeric" as const,
  };

  if (filters.dateRange?.start && filters.dateRange?.end) {
    const presetLabel = filters.dateRange.type
      ? DATE_RANGE_PRESET_LABELS[filters.dateRange.type]
      : undefined;

    if (compact) {
      const start = formatDate(
        toDateString(filters.dateRange.start),
        dateFormatOpts
      );
      const end = formatDate(
        toDateString(filters.dateRange.end),
        dateFormatOpts
      );
      summarizedFilters.push({
        label: "Date range",
        value: presetLabel ?? `${start} - ${end}`,
      });
    } else {
      const start = formatDate(
        toDateString(filters.dateRange.start),
        dateFormatOpts
      );
      const end = formatDate(
        toDateString(filters.dateRange.end),
        dateFormatOpts
      );
      const dateValue = presetLabel
        ? `${presetLabel} (${start} - ${end})`
        : `${start} - ${end}`;
      summarizedFilters.push({ label: "Date range", value: dateValue });
    }
  }

  if (filters.callNameSearch) {
    summarizedFilters.push({ label: "Search", value: filters.callNameSearch });
  }

  if (filters.durationRange) {
    const {
      minSeconds = DEFAULT_DURATION_MIN,
      maxSeconds = DEFAULT_DURATION_MAX,
    } = filters.durationRange;
    if (
      minSeconds !== DEFAULT_DURATION_MIN ||
      maxSeconds !== DEFAULT_DURATION_MAX
    ) {
      summarizedFilters.push({
        label: "Duration",
        value: `${formatDuration(minSeconds)} – ${formatDuration(maxSeconds)}`,
      });
    }
  }

  if (filters.interviewType === "screen" || filters.interviewType === "core") {
    summarizedFilters.push({
      label: "Type",
      value: filters.interviewType === "screen" ? "Screen" : "Core",
    });
  }

  const isDefaultMaxCount = nonDefaultOnly && filters.maxInterviewCount === 50;
  if (
    filters.maxInterviewCount != null &&
    [50, 100, 200].includes(filters.maxInterviewCount) &&
    !isDefaultMaxCount
  ) {
    summarizedFilters.push({
      label: "Max interviews",
      value: compact
        ? `${filters.maxInterviewCount} interviews`
        : String(filters.maxInterviewCount),
    });
  }

  const arrayFields: Array<{
    items: readonly DisplayableItem[] | undefined;
    label: string;
    allLabel: string;
  }> = [
    {
      items: filters.selectedPositions,
      label: "Positions",
      allLabel: "All positions",
    },
    {
      items: filters.selectedInterviewers,
      label: "Interviewers",
      allLabel: "All interviewers",
    },
    {
      items: filters.selectedDepartments,
      label: "Departments",
      allLabel: "All departments",
    },
    { items: filters.selectedStages, label: "Stages", allLabel: "All stages" },
    {
      items: filters.selectedLocations,
      label: "Locations",
      allLabel: "All locations",
    },
    {
      items: filters.selectedCandidateOutcomes,
      label: "Candidate outcomes",
      allLabel: "All outcomes",
    },
  ];
  arrayFields.forEach(({ items, label, allLabel }) => {
    if (items && items.length > 0) {
      summarizedFilters.push(getMultiFilterOptions(label, items));
    } else if (compact && !nonDefaultOnly) {
      summarizedFilters.push({ label, value: allLabel });
    }
  });
  return summarizedFilters;
}

type CustomInsightsFilterSummaryProps = {
  filters: SerializableFilterState;
  colorScheme?: string;
  filled?: boolean;
  /** When true, shows simplified value-only tags with "All X" defaults for unselected filters */
  compact?: boolean;
  /** When true, only shows pills for filters that differ from their default values */
  nonDefaultOnly?: boolean;
};

export const CustomInsightsFilterSummary: FC<
  CustomInsightsFilterSummaryProps
> = ({
  filters,
  colorScheme = "gray",
  filled = true,
  compact = false,
  nonDefaultOnly = false,
}) => {
  const items = useMemo(
    () => buildFilterSummary(filters, compact, nonDefaultOnly),
    [filters, compact, nonDefaultOnly]
  );
  const additionalProps = filled
    ? {}
    : {
        bg: "white",
        color: "gray.900",
        borderWidth: "1px",
        borderColor: "gray.200",
      };

  if (items.length === 0) return null;

  return (
    <Flex gap="4px" align="center" flexWrap="wrap" minW={0}>
      {items.map((item) => (
        <Tag
          key={item.label}
          size="sm"
          colorScheme={colorScheme}
          fontSize="11.67px"
          fontWeight="medium"
          lineHeight="16px"
          borderRadius="25px"
          px="8px"
          py="3px"
          {...additionalProps}
        >
          <Tooltip label={item.hoverText} shouldWrapChildren>
            {compact ? item.value : `${item.label}: ${item.value}`}
          </Tooltip>
        </Tag>
      ))}
    </Flex>
  );
};
